import { useState, useEffect } from 'react';

export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isSmallLaptop: boolean;
  isTouchDevice: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  windowWidth: number;
  windowHeight: number;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  orientation: 'portrait' | 'landscape';
}

/**
 * Custom hook to detect device type and screen properties
 * @returns DeviceInfo object with detailed device and screen information
 */
export const useDeviceDetection = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => 
    getDeviceInfo()
  );

  useEffect(() => {
    const handleResize = () => {
      setDeviceInfo(getDeviceInfo());
    };

    const handleOrientationChange = () => {
      // Small delay to ensure dimensions are updated after rotation
      setTimeout(() => {
        setDeviceInfo(getDeviceInfo());
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  return deviceInfo;
};

/**
 * Get device information based on user agent and screen dimensions
 * @returns DeviceInfo object
 */
function getDeviceInfo(): DeviceInfo {
  // Default values for SSR
  if (typeof window === 'undefined') {
    return {
      isMobile: false,
      isTablet: false,
      isDesktop: true,
      isSmallLaptop: false,
      isTouchDevice: false,
      isIOS: false,
      isAndroid: false,
      windowWidth: 1024,
      windowHeight: 768,
      deviceType: 'desktop',
      orientation: 'landscape',
    };
  }

  const userAgent = navigator.userAgent.toLowerCase();
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  // Detect specific mobile operating systems
  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  const isAndroid = /android/.test(userAgent);

  // Detect tablet via user agent (more specific patterns)
  const isTabletUA = /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent);

  // Detect mobile device via user agent (excluding tablets)
  const isMobileUA = /iphone|ipod|android.*mobile|webos|blackberry|iemobile|opera mini/i.test(userAgent);

  // Detect touch capability
  const isTouchDevice = 
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0;

  // Screen size based detection (as a fallback and refinement)
  const isMobileSize = windowWidth < 768; // Phones typically < 768px
  const isTabletSize = windowWidth >= 768 && windowWidth < 1024; // Tablets 768-1023px

  // UA-based detection is trusted unconditionally (covers real devices AND DevTools emulation
  // which spoofs the UA string when a device is selected). isTouchDevice is only used as an
  // additional gate for the screen-size fallback, to prevent a laptop that just has its
  // browser window resized from being misclassified.
  const isMobileOrTabletOS = isIOS || isAndroid;
  let isTablet = isTabletUA || (isTouchDevice && isTabletSize && isMobileOrTabletOS);
  let isMobile = !isTablet && (isMobileUA || (isTouchDevice && isMobileSize && isMobileOrTabletOS));

  // iPad detection (iOS 13+ reports as desktop Safari in user agent but isTouchDevice on real device)
  if (isIOS && isTouchDevice && windowWidth >= 768) {
    isTablet = true;
    isMobile = false;
  }

  // Ensure mutual exclusivity
  if (isTablet) {
    isMobile = false;
  }

  const isDesktop = !isMobile && !isTablet;

  // Determine device type
  let deviceType: 'mobile' | 'tablet' | 'desktop';
  if (isMobile) {
    deviceType = 'mobile';
  } else if (isTablet) {
    deviceType = 'tablet';
  } else {
    deviceType = 'desktop';
  }

  // Determine orientation
  const orientation: 'portrait' | 'landscape' = 
    windowHeight > windowWidth ? 'portrait' : 'landscape';

  // Small laptop: desktop screens between 1024px and 1280px wide
  const isSmallLaptop = isDesktop && windowWidth >= 1024 && windowWidth < 1280;

  return {
    isMobile,
    isTablet,
    isDesktop,
    isSmallLaptop,
    isTouchDevice,
    isIOS,
    isAndroid,
    windowWidth,
    windowHeight,
    deviceType,
    orientation,
  };
}

/**
 * Utility function to check if running on mobile device (non-hook version)
 * Useful for one-time checks outside of React components
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  const userAgent = navigator.userAgent.toLowerCase();
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
};

/**
 * Utility function to check screen size breakpoints
 */
export const getBreakpoint = (width: number): 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' => {
  if (width < 640) return 'xs';
  if (width < 768) return 'sm';
  if (width < 1024) return 'md';
  if (width < 1280) return 'lg';
  if (width < 1536) return 'xl';
  return '2xl';
};
